import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getBlogArticles, getBlogArticle, createBlogArticle, updateBlogArticle, deleteBlogArticle,
  getBlogCategories, createBlogCategory, getBlogTags, createBlogTag,
  uploadBlogImage, deleteBlogImage, updateBlogArticleFeaturedImage,
  generateBlogArticles,
} from '../../api';
import toast from 'react-hot-toast';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiEye, FiEyeOff,
  FiUpload, FiBold, FiItalic, FiList, FiLink,
  FiArrowLeft, FiSave, FiImage, FiZap,
} from 'react-icons/fi';
import './AdminBlog.css';

/* ── RICH TEXT EDITOR ── */
const RichEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value;
      isInitialized.current = true;
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handleInput = (e) => onChange(e.currentTarget.innerHTML);

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) exec('insertImage', url);
  };

  return (
    <div className="rich-editor">
      <div className="rich-toolbar">
        <button type="button" title="Bold" onClick={() => exec('bold')}><FiBold /></button>
        <button type="button" title="Italic" onClick={() => exec('italic')}><FiItalic /></button>
        <button type="button" title="H2" onClick={() => exec('formatBlock', 'h2')} className="toolbar-text">H2</button>
        <button type="button" title="H3" onClick={() => exec('formatBlock', 'h3')} className="toolbar-text">H3</button>
        <button type="button" title="Paragraph" onClick={() => exec('formatBlock', 'p')} className="toolbar-text">P</button>
        <div className="toolbar-sep" />
        <button type="button" title="Bullet List" onClick={() => exec('insertUnorderedList')}><FiList /></button>
        <button type="button" title="Ordered List" onClick={() => exec('insertOrderedList')} className="toolbar-text">1.</button>
        <button type="button" title="Blockquote" onClick={() => exec('formatBlock', 'blockquote')} className="toolbar-text">"</button>
        <div className="toolbar-sep" />
        <button type="button" title="Link" onClick={insertLink}><FiLink /></button>
        <button type="button" title="Image URL" onClick={insertImage}><FiImage /></button>
        <div className="toolbar-sep" />
        <button type="button" title="Clear formatting" onClick={() => exec('removeFormat')} className="toolbar-text">T</button>
      </div>
      <div
        ref={editorRef}
        className="rich-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
      />
    </div>
  );
};

/* ── ARTICLE FORM ── */
const ArticleForm = ({ article, categories, tags, onSaved, onCancel }) => {
  const isEdit = !!article;
  const [form, setForm] = useState({
    title: article?.title || '',
    content: article?.content || '',
    meta_title: article?.meta_title || '',
    meta_description: article?.meta_description || '',
    category: article?.category?.id || article?.category || '',
    tags: article?.tags?.map((t) => t.id || t) || [],
    is_published: article?.is_published ?? false,
    featured_image: article?.featured_image || '',
  });
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState(article?.images || []);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [newTag, setNewTag] = useState('');
  const [localCategories, setLocalCategories] = useState(categories);
  const [localTags, setLocalTags] = useState(tags);

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleTag = (tagId) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tagId) ? f.tags.filter((t) => t !== tagId) : [...f.tags, tagId],
    }));
  };

  const handleImgUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !article) { toast.error('Save article first before uploading images'); return; }
    setUploadingImg(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('article', article.id);
        fd.append('image', file);
        fd.append('alt', file.name.replace(/\.[^.]+$/, ''));
        fd.append('order', images.length);
        const { data } = await uploadBlogImage(fd);
        setImages((prev) => [...prev, data]);
      }
      toast.success('Images uploaded!');
    } catch { toast.error('Image upload failed'); }
    finally { setUploadingImg(false); }
  };

  const handleDeleteImg = async (id) => {
    await deleteBlogImage(id);
    setImages((prev) => prev.filter((i) => i.id !== id));
    toast.success('Image removed');
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFeatured(true);
    try {
      const { data } = await updateBlogArticleFeaturedImage(article.slug, file);
      setForm((f) => ({ ...f, featured_image: data.featured_image }));
      toast.success('Featured image uploaded!');
    } catch (err) {
      console.error('Featured image error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.featured_image?.[0] || 
                       err.response?.data?.detail ||
                       err.response?.data?.[0] ||
                       err.message ||
                       'Featured image upload failed';
      toast.error(errorMsg);
    } finally {
      setUploadingFeatured(false);
    }
  };

  const handleRemoveFeaturedImage = async () => {
    try {
      await updateBlogArticle(article.slug, { featured_image: null });
      setForm((f) => ({ ...f, featured_image: '' }));
      toast.success('Featured image removed');
    } catch {
      toast.error('Failed to remove featured image');
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    const { data } = await createBlogCategory({ name: newCat.trim(), description: '' });
    setLocalCategories((prev) => [...prev, data]);
    setNewCat('');
    toast.success('Category added');
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    const { data } = await createBlogTag({ name: newTag.trim() });
    setLocalTags((prev) => [...prev, data]);
    setNewTag('');
    toast.success('Tag added');
  };

  const handleSave = async (publish = null) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const { featured_image, ...payloadData } = form;
      const payload = {
        ...payloadData,
        is_published: publish !== null ? publish : form.is_published,
      };
      if (!isEdit) payload.slug = slugify(form.title);
      if (isEdit) await updateBlogArticle(article.slug, payload);
      else await createBlogArticle(payload);
      toast.success(isEdit ? 'Article updated!' : 'Article created!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="article-form">
      <div className="article-form-header">
        <button className="btn btn-ghost" onClick={onCancel}><FiArrowLeft /> Back</button>
        <h2>{isEdit ? 'Edit Article' : 'New Article'}</h2>
        <div className="article-form-actions">
          <button className="btn btn-outline" onClick={() => handleSave(false)} disabled={saving}>
            <FiEyeOff /> Save Draft
          </button>
          <button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving}>
            <FiEye /> {saving ? 'Saving...' : 'Publish'}
          </button>
        </div>
      </div>

      <div className="article-form-layout">
        {/* Main content */}
        <div className="article-form-main">
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input name="title" value={form.title} onChange={handleChange} placeholder="Article title..." style={{ fontSize: 20, fontWeight: 700 }} />
          </div>

          <div className="form-group">
            <label className="form-label">Meta Description (shown in search results)</label>
            <textarea name="meta_description" value={form.meta_description} onChange={handleChange} rows={2} placeholder="Brief description of the article..." />
          </div>

          <div className="form-group">
            <label className="form-label">Content *</label>
            <RichEditor value={form.content} onChange={(v) => setForm((f) => ({ ...f, content: v }))} />
          </div>

          {/* Featured Image */}
          <div className="form-group">
            <label className="form-label">Featured Image (shown on blog list)</label>
            {form.featured_image && (
              <div style={{ marginBottom: 12, position: 'relative', display: 'inline-block' }}>
                <img src={form.featured_image} alt="Featured" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8 }} />
                {isEdit && (
                  <button
                    type="button"
                    onClick={handleRemoveFeaturedImage}
                    style={{
                      position: 'absolute', top: -8, right: -8,
                      background: 'var(--dji-red)', border: 'none',
                      borderRadius: '50%', width: 24, height: 24,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: 'white',
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            {isEdit && (
              <label className="blog-upload-btn">
                <FiUpload /> {uploadingFeatured ? 'Uploading...' : 'Upload Featured Image'}
                <input type="file" accept="image/*" onChange={handleFeaturedImageUpload} style={{ display: 'none' }} disabled={uploadingFeatured} />
              </label>
            )}
          </div>

          {/* Images */}
          <div className="form-group">
            <label className="form-label">
              Article Images
              {!isEdit && <span style={{ color: 'var(--dji-muted)', marginLeft: 8, fontWeight: 400 }}>(save article first)</span>}
            </label>
            {images.length > 0 && (
              <div className="blog-img-grid">
                {images.map((img) => (
                  <div key={img.id} className="blog-img-thumb">
                    <img src={img.image} alt={img.alt || ''} />
                    <button className="blog-img-del" onClick={() => handleDeleteImg(img.id)}><FiTrash2 /></button>
                    {img.caption && <p className="blog-img-cap">{img.caption}</p>}
                  </div>
                ))}
              </div>
            )}
            {isEdit && (
              <label className="blog-upload-btn">
                <FiUpload /> {uploadingImg ? 'Uploading...' : 'Upload Images'}
                <input type="file" accept="image/*" multiple onChange={handleImgUpload} style={{ display: 'none' }} disabled={uploadingImg} />
              </label>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="article-form-sidebar">
          {/* Status */}
          <div className="sidebar-card">
            <h4>Status</h4>
            <label className="checkbox-label">
              <input type="checkbox" name="is_published" checked={form.is_published} onChange={handleChange} />
              Published (visible to public)
            </label>
          </div>

          {/* Category */}
          <div className="sidebar-card">
            <h4>Category</h4>
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="">-- No Category --</option>
              {localCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="add-inline">
              <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category..." onKeyDown={(e) => e.key === 'Enter' && addCategory()} />
              <button className="btn btn-primary btn-sm" onClick={addCategory}><FiPlus /></button>
            </div>
          </div>

          {/* Tags */}
          <div className="sidebar-card">
            <h4>Tags</h4>
            <div className="tags-select">
              {localTags.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`tag-toggle ${form.tags.includes(t.id) ? 'selected' : ''}`}
                  onClick={() => toggleTag(t.id)}
                >
                  #{t.name}
                </button>
              ))}
            </div>
            <div className="add-inline">
              <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="New tag..." onKeyDown={(e) => e.key === 'Enter' && addTag()} />
              <button className="btn btn-primary btn-sm" onClick={addTag}><FiPlus /></button>
            </div>
          </div>

          {/* SEO */}
          <div className="sidebar-card">
            <h4>SEO</h4>
            <div className="form-group">
              <label className="form-label">Meta Title</label>
              <input name="meta_title" value={form.meta_title} onChange={handleChange} placeholder="SEO title..." />
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleSave()} disabled={saving}>
            <FiSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── ADMIN BLOG MAIN ── */
const AdminBlog = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, 'new' | article = form
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { if (!isAdmin) navigate('/'); }, [isAdmin, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c, t] = await Promise.all([getBlogArticles({ page_size: 100 }), getBlogCategories(), getBlogTags()]);
      setArticles(a.data.results || []);
      setCategories(c.data.results || []);
      setTags(t.data.results || []);
    } catch { toast.error('Error loading blog data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (slug) => {
    if (!window.confirm('Delete this article?')) return;
    await deleteBlogArticle(slug);
    setArticles((prev) => prev.filter((a) => a.slug !== slug));
    toast.success('Article deleted');
  };

  const handleEdit = async (article) => {
    setLoadingEdit(true);
    try {
      const { data } = await getBlogArticle(article.slug);
      setEditing(data);
    } catch (err) {
      toast.error('Failed to load article for editing');
      console.error(err);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleGenerate = async () => {
    if (generating) return;
    if (!window.confirm('Generate 3 SEO blog drafts from the latest news using AI? They will be saved as drafts for you to review.')) return;
    setGenerating(true);
    try {
      const { data } = await generateBlogArticles(3);
      toast.success(data?.detail || 'Generating drafts in the background…');
      // Generation runs in the background on the server; refresh a couple of times.
      setTimeout(fetchAll, 60000);
      setTimeout(fetchAll, 150000);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Failed to start generation');
    } finally {
      setGenerating(false);
    }
  };

  if (editing !== null) {
    return (
      <div className="admin-blog-page">
        <ArticleForm
          article={editing === 'new' ? null : editing}
          categories={categories}
          tags={tags}
          onSaved={() => { fetchAll(); setEditing(null); }}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="admin-blog-page">
      <div className="admin-blog-header">
        <div>
          <h1 className="page-title">Blog Manager</h1>
          <p className="text-muted">{articles.length} articles total</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={handleGenerate} disabled={generating} title="Draft 2-3 SEO articles from the latest news with AI">
            <FiZap /> {generating ? 'Starting…' : 'Generate with AI'}
          </button>
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <FiPlus /> New Article
          </button>
        </div>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 300 }} />
      ) : articles.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📝</div>
          <p>No articles yet. Create your first one!</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setEditing('new')}>
            <FiPlus /> Write Article
          </button>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Title</th><th>Category</th><th>Tags</th>
                  <th>Status</th><th>Views</th><th>Date</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id}>
                    <td><strong>{a.title}</strong></td>
                    <td>{a.category?.name || a.category || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {a.tags?.slice(0, 3).map((t) => (
                          <span key={t.id || t} style={{ fontSize: 11, background: 'var(--dji-dark)', border: '1px solid var(--dji-gray)', padding: '2px 6px', borderRadius: 3, color: 'var(--dji-muted)' }}>
                            #{t.name || t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${a.is_published ? 'badge-green' : 'badge-yellow'}`}>
                        {a.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>{a.views || 0}</td>
                    <td>{new Date(a.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="action-btn edit" onClick={() => handleEdit(a)} disabled={loadingEdit} title="Edit"><FiEdit2 /></button>
                        <button className="action-btn delete" onClick={() => handleDelete(a.slug)} title="Delete"><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
